import React, { useEffect, useState } from "react";
import { FaCheck, FaFileExcel, FaTimes, FaCaretDown } from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { API_BASE_URL } from "../../utils/CONSTANTS";
import Header from "../../components/header";

const PackingRegister = () => {
  const [filters, setFilters] = useState({
    startDate: new Date().toISOString().split("T")[0],
    endDate: new Date().toISOString().split("T")[0],
  });

  const [data, setData] = useState([]);

  // Sorting state & logic
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedData = React.useMemo(() => {
    if (!data) return [];
    const sortableItems = [...data];

    if (!sortConfig.key) return sortableItems;

    const key = sortConfig.key;
    const dir = sortConfig.direction;

    sortableItems.sort((a, b) => {
      const aVal = a?.[key] ?? "";
      const bVal = b?.[key] ?? "";

      // numeric comparison
      if (!isNaN(Number(aVal)) && !isNaN(Number(bVal))) {
        return dir === "asc" ? Number(aVal) - Number(bVal) : Number(bVal) - Number(aVal);
      }

      // date-like keys
      if (key.toLowerCase().includes("date")) {
        const ta = aVal ? new Date(aVal).getTime() : 0;
        const tb = bVal ? new Date(bVal).getTime() : 0;
        return dir === "asc" ? ta - tb : tb - ta;
      }

      // fallback string compare
      return dir === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });

    return sortableItems;
  }, [data, sortConfig]);

  useEffect(() => {
    fetchData();
  }, [filters]);

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const fetchData = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/report/packingall`,
        {
          params: filters,
        },
        {
          withCredentials: true,
        }
      );

      if (response.status === 200) {
        toast("Data fetched successfully");
        setData(response.data.packages);
      }
    } catch (err) {
      console.error("Error fetching data:", err);
    }
  };

  const handleShow = () => {
    fetchData(); // Call the fetch function when Show is clicked
  };

  const handleExport = () => {
    const formattedData = data.map((row) => ({
      Party: row.packerName,
      "Card No": row.cardNo,
      "Drg. No.": row.drgNo,
      "Pack Qty": row.totalquantity,
      Date: new Date(row.date).toLocaleDateString().split("T")[0],
      "Wt.": row.wt,
      Packer: row.packerName,
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Packing Register");

    XLSX.writeFile(workbook, "PackingRegister.xlsx");
  };

  return (
    <>
      <Header />

      <div className="p-2 max-w-[1200px] mx-auto">
        <div className="bg-[#e6ffff] border border-gray-300 rounded shadow">
          {/* Header */}
          <div className="flex justify-between items-center px-2 py-1 border-b border-gray-300">
            <h2 className="text-sm font-semibold">Packing Register</h2>
            <div className="flex gap-1">
              <button className="p-0.5 hover:bg-gray-200 rounded">
                <FaTimes className="text-gray-600 w-3 h-3" />
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-2 gap-2 p-2 bg-[#e6ffff]">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-0.5">
                Start Date
              </label>
              <div className="relative">
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) =>
                    handleFilterChange("startDate", e.target.value)
                  }
                  className="w-full border rounded p-1 pr-6 text-xs"
                />
                <FaCaretDown className="absolute right-1.5 top-2 text-gray-400 w-2 h-2" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-0.5">
                End Date
              </label>
              <div className="relative">
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange("endDate", e.target.value)}
                  className="w-full border rounded p-1 pr-6 text-xs"
                />
                <FaCaretDown className="absolute right-1.5 top-2 text-gray-400 w-2 h-2" />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="p-2">
            <div className="border rounded bg-white">
              <table className="w-full text-xs overflow-x-auto">
                <thead>
                  <tr className="border-b">
                    <th onClick={() => handleSort("partyName")} className="border-r p-1 text-left font-medium cursor-pointer">
                      Party {sortConfig.key === "partyName" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
                    </th>

                    <th onClick={() => handleSort("cardNo")} className="border-r p-1 text-left font-medium cursor-pointer">
                      Card No {sortConfig.key === "cardNo" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
                    </th>

                    <th onClick={() => handleSort("drgNo")} className="border-r p-1 text-left font-medium cursor-pointer">
                      Drg. No. {sortConfig.key === "drgNo" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
                    </th>

                    <th onClick={() => handleSort("totalquantity")} className="border-r p-1 text-left font-medium cursor-pointer">
                      Pack Qty {sortConfig.key === "totalquantity" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
                    </th>

                    <th onClick={() => handleSort("date")} className="border-r p-1 text-left font-medium cursor-pointer">
                      Date {sortConfig.key === "date" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
                    </th>

                    <th onClick={() => handleSort("wt")} className="border-r p-1 text-left font-medium cursor-pointer">
                      Wt. {sortConfig.key === "wt" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
                    </th>

                    <th onClick={() => handleSort("packerName")} className="p-1 text-left font-medium cursor-pointer">
                      Packer {sortConfig.key === "packerName" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedData.map((row, index) => (
                    <tr key={index} className="border-b">
                      <td className="border-r p-1">{row.partyName}</td>
                      <td className="border-r p-1">{row.cardNo}</td>
                      <td className="border-r p-1">{row.drgNo}</td>
                      <td className="border-r p-1">{row.totalquantity}</td>
                      <td className="border-r p-1">{row.date?.split("T")[0]}</td>
                      <td className="border-r p-1">{row.wt}</td>
                      <td className="p-1">{row.packerName}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-between p-2 bg-teal-600">
            <button
              onClick={handleShow}
              className="flex items-center gap-1 bg-white px-3 py-1 rounded hover:bg-gray-50 text-xs">
              <FaCheck className="text-green-600 w-3 h-3" />
              <span>Show</span>
            </button>

            <button
              onClick={handleExport}
              className="flex items-center gap-1 bg-white px-3 py-1 rounded hover:bg-gray-50 text-xs">
              <FaFileExcel className="text-green-600 w-3 h-3" />
              <span>Export to Excel</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default PackingRegister;
